import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

/**
 * Cliente HTTP del backend Foxbox Depósito.
 *
 * - Base URL desde VITE_API_BASE_URL (default `/api` para que el proxy de Vite resuelva en dev).
 * - Inyecta JWT desde localStorage en cada request.
 * - Normaliza errores → siempre `{ status, code, message, details? }`.
 */

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TOKEN_KEY = 'foxbox.dep.token';

export function setAuthToken(token: string | null) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAuthToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

export interface ApiError {
  status: number;
  code: string;
  message: string;
  details?: unknown;
}

api.interceptors.response.use(
  (resp) => resp,
  (err: AxiosError<{ code?: string; message?: string; details?: unknown }>) => {
    const normalized: ApiError = {
      status: err.response?.status ?? 0,
      code: err.response?.data?.code ?? err.code ?? 'NETWORK_ERROR',
      message:
        err.response?.data?.message ??
        err.message ??
        'Error desconocido contactando el backend',
      details: err.response?.data?.details,
    };
    return Promise.reject(normalized);
  },
);

export type { AxiosResponse } from 'axios';
